import React from 'react';
import queryString from 'query-string';
import { getParser } from 'bowser/es5';

import decodeBase64Unicode from '../../core/decodeBase64Unicode';
import { Language, languages } from '../../core/i18n';

import OceanDragonInvitePage from '../OceanDragonInvitePage';
import SecondAnniversaryPage from '../2ndAnniversary';
import MoonLight from '../MoonLight';
import FireSpirit from '../FireSpirit';
import RainbowCube from '../RainbowCube';
import JellySkin from '../JellySkin';
import Yeti from '../Yeti';
import DreamCatcher from '../DreamCatcher';
import WaveDrop from '../WaveDrop';
import JellySkin02 from '../JellySkin02';

const platforms = ['ios', 'android'] as const;
export type Platform = typeof platforms[number];

const seasons = [
  'OceanDragon',
  '2ndAnniversary',
  'MoonLight',
  'FireSpirit',
  'RainbowCube',
  'JellySkin',
  'Yeti',
  'DreamCatcher',
  'WaveDrop',
  'JellySkin02',
] as const;
export type Season = typeof seasons[number];

const prevSeason = seasons[8];

const currentSeasonStart = new Date('2020-12-31T09:00:00+09:00');
const oneLinkUrlDeprecatedDate = new Date('2021-03-26T09:00:00+09:00');
const currentSeason = seasons[9];

// 앱으로부터 Base64 인코딩을 통해 URL Search 인자들이 전달되고 있다
// Base64는 URL-Safe하지 않다.
// 임시 방편으로 직접 치환한다.
const makeSafeUri = (text: string) => text.replace(
  /&n=([a-zA-Z0-9+-/&=]+?)/,
  (_, nickname) => `&n=${encodeURIComponent(nickname)}`,
);

const currentBrowser = getParser(navigator.userAgent);
const isValidLanguage = (language: string): language is Language =>
  languages.indexOf(language as any) !== -1;
const isValidSeason = (season: string): season is Season =>
  seasons.indexOf(season as any) !== -1;
const isValidPlatform = (platform: string): platform is Platform =>
  platforms.indexOf(platform as any) !== -1;

export interface InvitePageProps {
  mid: string;
  nickname: string;
  platform: Platform;
  language: Language;
}

interface AppState {
  profile: {
    mid: string;
    nickname: string;
  };
  language?: Language;
  season?: Season;
  platform: Platform;
}
class App extends React.Component<{}, AppState> {
  private static getState = (): AppState => {
    // extract profile
    const { m: mid, n } = queryString.parse(makeSafeUri(window.location.search));
    const nickname = decodeBase64Unicode(decodeURIComponent(n));
    const profile = { mid, nickname };

    // extract language
    const [, language, season] = window.location.pathname.split('/');

    // detect platform
    const platform = currentBrowser.getOSName(true);

    return {
      profile,
      language: isValidLanguage(language) ? language : languages[0],
      season: isValidSeason(season) ? season : seasons.slice(-1)[0],
      platform: isValidPlatform(platform) ? platform : platforms.slice(-1)[0],
    };
  }

  private isResizingWindow: boolean = false;

  constructor(props: {}) {
    super(props);
    this.state = App.getState();
  }

  public componentDidMount(): void {
    const targetSeason = currentSeasonStart > new Date() ? prevSeason : currentSeason;
    const isDeprecated = oneLinkUrlDeprecatedDate <= new Date();
    if (isDeprecated) {
      window.location.replace(`https://ovenbreak.onelink.me/657050516/73a8c62c?mid=${this.state.profile.mid}`);
    } else {
      window.location.replace(`https://ovenbreak.onelink.me/657050516/73a8c62c?deep_link_action=invite&deep_link_value=${this.state.profile.mid}`);
    }
    if ([
      // 개발모드에서는 리디렉션 끔
      process.env.NODE_ENV === 'production',
      // currentSeason 굳이 리디렉션 하지 않음
      this.state.season !== currentSeason,
      this.state.season !== targetSeason,
    ].every(Boolean)) {
      window.location.href = `/${this.state.language}/${targetSeason}${window.location.search}`;
    }
    window.addEventListener('resize', this.onWindowResize);
    this.onWindowResize();
  }

  public componentWillUnmount(): void {
    window.removeEventListener('resize', this.onWindowResize);
  }

  public render() {
    if (!this.isRenderable()) {
      return null;
    }
    const {
      profile,
      platform,
      language,
      season,
    } = this.state;
    const PageComponent: React.ComponentType<InvitePageProps> | null = (
      season === 'OceanDragon' ? OceanDragonInvitePage :
        season === '2ndAnniversary' ? SecondAnniversaryPage :
          season === 'MoonLight' ? MoonLight :
            season === 'FireSpirit' ? FireSpirit :
              season === 'RainbowCube' ? RainbowCube :
                season === 'JellySkin' ? JellySkin :
                  season === 'Yeti' ? Yeti :
                    season === 'DreamCatcher' ? DreamCatcher :
                      season === 'WaveDrop' ? WaveDrop :
                        season === 'JellySkin02' ? JellySkin02 :
                          null
    );
    return PageComponent ? (
      <PageComponent
        {...profile}
        language={language as Language}
        platform={platform}
      />
    ) : null;
  }

  private onWindowResize = (): void => {
    if (this.isResizingWindow) {
      return;
    }
    this.isResizingWindow = true;
    requestAnimationFrame(() => {
      const { documentElement } = document;
      const fontSize = documentElement!.clientWidth / 640;
      const actualFontSize = (fontSize > 1) ? 1 : fontSize;
      documentElement!.style.fontSize = `${actualFontSize}px`;
      this.isResizingWindow = false;
    });
  }

  private isRenderable = (): boolean => Boolean(this.state.language);
}

export default App;
